import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getTheme, setTheme, resolveTheme } from '@/lib/theme';
import { Sun, Moon, Monitor, Check } from 'lucide-react';

const OPTIONS = [
  { key: 'light', label: 'Light', Icon: Sun },
  { key: 'dark', label: 'Dark', Icon: Moon },
  { key: 'system', label: 'System', Icon: Monitor },
];

export default function BrandBar() {
  const item = {
    fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase',
    textDecoration: 'none', padding: '0 12px',
    borderRight: '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer',
  };
  const handleNav = (url) => () => window.open(url, '_blank', 'noopener,noreferrer');

  const { user } = useAuth();
  const [pref, setPref] = useState(getTheme);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // A dropdown rather than a three-way cycle: cycling makes you guess, and
  // "System" is precisely the option people need to SEE exists — a cycle hides
  // it until the second click.
  const choose = (key) => { setPref(setTheme(key)); setOpen(false); };

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // The trigger shows what is APPLIED, so "System" still reads as a sun or moon
  // and the label says which mode the OS resolved to.
  const applied = resolveTheme(pref);
  const TriggerIcon = pref === 'system' ? Monitor : (applied === 'dark' ? Moon : Sun);
  const triggerLabel = pref === 'system' ? `System · ${applied}` : (applied === 'dark' ? 'Dark' : 'Light');

  return (
    <div className="brand-bar-fixed" style={{
      background: '#0a0907',
      borderBottom: '1px solid rgba(43,179,168,0.06)',
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 60,
      display: 'flex', alignItems: 'center', paddingLeft: '8px',
      fontFamily: "'DM Sans', sans-serif",
    }} data-testid="brand-bar">
      {/* Pixally sits first, Uncut Gems second. The TEAL/bold/not-clickable
          treatment travels with Uncut Gems rather than with the first slot —
          it marks the property you are currently in, not the leftmost item.
          Teal, not gold, since 2026-09-26: the same bar on uncutgems.com. */}
      <span onClick={handleNav('https://pixally.com')} style={{ ...item, color: 'rgba(255,255,255,0.6)' }}>Pixally</span>
      <span style={{ ...item, color: '#2bb3a8', fontWeight: 'bold', cursor: 'default' }}>Uncut Gems</span>

      {/* Right-hand chrome. This wrapper is the direct child that
          .brand-bar-fixed > * { height:32px } applies to, so everything nested
          below is free of that override. #brandbar-actions is an empty slot the
          authenticated Layout portals its notification bell into — the bell has
          to live next to the theme control, but its data and routing belong to
          Layout, so it is rendered there and placed here. */}
      <div style={{ marginLeft: 'auto', marginRight: '10px', display: 'flex', alignItems: 'center', gap: '7px', height: '32px' }}>
        <div id="brandbar-actions" style={{ display: 'flex', alignItems: 'center' }} />
      {/* Appearance is for signed-in pages only. Every page a signed-out visitor sees is dark whatever the choice
          (useForceDark), so the control there changed nothing (operator, 2026-09-27). */}
      {user && (
      <div ref={wrapRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '32px' }}>
        <button
          onClick={() => setOpen(o => !o)}
          data-testid="theme-toggle-btn"
          /* tap-auto: documented opt-out from the global
             [data-testid] { min-height: 44px } rule (see CLAUDE.md). The button
             used to be a direct child of .brand-bar-fixed, whose
             > * { height:32px !important } overrode that min-height. Wrapping it
             for the dropdown moved that override onto the wrapper, so the button
             inflated to 44px inside a 32px bar and the label was pushed down. */
          className="tap-auto brandbar-pill"
          aria-haspopup="menu"
          aria-expanded={open}
          title="Appearance"
          style={{ padding: '0 9px 0 4px', gap: '6px' }}>
          {/* The orb is the friendly bit: a little sun or moon that warms and
              tilts on hover. Colour follows what is APPLIED, not the preference. */}
          <span className="brandbar-orb" style={{
            background: applied === 'dark'
              ? 'radial-gradient(circle at 35% 30%, #cfd8ea, #7d89a8)'
              : 'radial-gradient(circle at 35% 30%, #c9f3ee, #2bb3a8)',
            boxShadow: applied === 'dark'
              ? '0 0 7px rgba(160,175,205,0.55)'
              : '0 0 7px rgba(43,179,168,0.6)',
          }}>
            <TriggerIcon size={9} color={applied === 'dark' ? '#1b2030' : '#0c3a36'} strokeWidth={2.4} />
          </span>
          <span style={{ fontSize: '8px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, lineHeight: 1 }}>{triggerLabel}</span>
        </button>

        {open && (
          /* position:fixed, not absolute. .brand-bar-fixed is 32px tall with
             overflow:hidden (index.css:73), which clips any absolutely
             positioned child to a 6px sliver. A fixed element's containing
             block is the viewport, and the bar sets no transform/filter, so it
             escapes the clip without changing shared CSS. right:10px matches
             the wrapper's own margin. */
          <div role="menu" data-testid="theme-menu" className="brandbar-menu" style={{
            position: 'fixed', top: '38px', right: '10px', minWidth: '132px', zIndex: 70,
          }}>
            {OPTIONS.map(({ key, label, Icon }) => (
              <button
                key={key}
                role="menuitemradio"
                aria-checked={pref === key}
                data-testid={`theme-option-${key}`}
                className="tap-auto brandbar-menu-item"
                data-chosen={pref === key ? 'true' : undefined}
                onClick={() => choose(key)}>
                {/* Same orb as the trigger, coloured for what the option would apply. */}
                <span className="brandbar-orb" style={{
                  background: key === 'dark'
                    ? 'radial-gradient(circle at 35% 30%, #cfd8ea, #7d89a8)'
                    : key === 'light'
                      ? 'radial-gradient(circle at 35% 30%, #c9f3ee, #2bb3a8)'
                      : 'radial-gradient(circle at 35% 30%, #e6e1d6, #8f8878)',
                  boxShadow: pref === key
                    ? (key === 'dark' ? '0 0 7px rgba(160,175,205,0.55)' : '0 0 7px rgba(43,179,168,0.6)')
                    : 'none',
                }}>
                  <Icon size={9} color={key === 'dark' ? '#1b2030' : '#0c3a36'} strokeWidth={2.4} />
                </span>
                <span className="brandbar-menu-label">{label}</span>
                {pref === key && <Check size={10} color="#2bb3a8" strokeWidth={2.6} />}
              </button>
            ))}
          </div>
        )}
      </div>
      )}
      </div>
    </div>
  );
}
